<template>
  <v-sheet
    color="white"
    elevation="5"
    rounded
    v-bind:style="`td { margin: 5px; }`" 
  >
      <v-dialog v-model="showDeleteConfirmationDialog" max-width="550px">
        <v-card>
          <v-card-title>
            <h3>Are you sure you want to delete show result?</h3>
          </v-card-title>
          <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="secondary" @click="showDeleteConfirmationDialog = false">
                Cancel
              </v-btn>
              <v-btn color="primary" @click="confirmDelete">
                 Delete
              </v-btn>
             <v-spacer />
           </v-card-actions>
        </v-card>
      </v-dialog>

      <v-alert v-if="!!errorMessage" type="error">
        {{ errorMessage }}
      </v-alert>  
      <v-row>        
        <v-col cols="4">
          <v-row>
            <v-col cols="8">
              <v-autocomplete
                label="Select a conformation entry to calculate scores (date • time • show type)"
                clearable
                v-model="selectedConformationEntry"
                :item-value="(item) => item"
                :item-text="(item) => `${$dateUtils.formatDate(item.showDate)}  •  ${item.showTime}  •  ${item.showType}`"
                :items="savedConformationEntries"
                :disabled="!savedConformationEntries || !savedConformationEntries.length" />
            </v-col>
            <v-col>
              <v-btn class="mt-2" icon v-on:click="calculatePoints" :disabled="!(!!selectedConformationEntry)" color="primary">
                <v-icon>mdi-calculator</v-icon>
              </v-btn> 
            </v-col>            
          </v-row>
        </v-col>
        <v-col>
          <v-row>
            <v-col cols="3">   
              <p v-if="!!calculatedPoints">
                <span><b>CH - Calculated scores: </b></span><br/>
                <span>WD - {{selectedConformationEntryDetails.winnerDogId?.trim()}} : {{calculatedPoints?.winnerDogScore == null ? 'N/A' : calculatedPoints.winnerDogScore}}</span><br/>
                <span>WB - {{selectedConformationEntryDetails.winnerBitchId?.trim()}} : {{calculatedPoints?.winnerBitchScore == null ? 'N/A' : calculatedPoints.winnerBitchScore}}</span><br/>
                <span>BOB - {{ selectedConformationEntryDetails.bestOfBreedId?.trim() }} : (#Cmp) {{calculatedPoints?.bestOfBreedScore == null ? 'N/A' : calculatedPoints.bestOfBreedScore }}</span><br/>
                <span>BOS - {{ selectedConformationEntryDetails.bestOfOppositeSexId?.trim()}} : (#Cmp) {{calculatedPoints?.bestOfOppositeSexScore == null ? 'N/A' : calculatedPoints.bestOfOppositeSexScore}}</span>
              </p>
            </v-col>
            <v-col cols="3">   
              <p v-if="!!calculatedPoints">
                <span><b>SCCH - Calculated scores: </b></span><br/>
                <span>BOB - {{ selectedConformationEntryDetails.bestOfBreedId?.trim() }} :  {{calculatedPoints?.scchBestOfBreedScore == null ? 'N/A' : calculatedPoints.scchBestOfBreedScore}}</span><br/>
                <span>BOS - {{ selectedConformationEntryDetails.bestOfOppositeSexId?.trim()}} : {{calculatedPoints?.scchBestOfOppositeSexScore == null ? 'N/A' : calculatedPoints.scchBestOfOppositeSexScore}}</span><br/>
                <span>SelD - {{ selectedConformationEntryDetails.selectDogId?.trim()}} : {{calculatedPoints?.selectDogScore == null ? 'N/A' : calculatedPoints.selectDogScore}}</span><br/>
                <span>SelB - {{ selectedConformationEntryDetails.selectBitchId?.trim()}} : {{calculatedPoints?.selectBitchScore == null ? 'N/A' : calculatedPoints.selectBitchScore}}</span>
              </p>
            </v-col>
            <v-col>   
              <p v-if="!!calculatedPoints">
                <span><b>{{selectedConformationEntry.showType}} - Calculated scores: </b></span><br/>
                <span v-for="dogId in uniqueDogIds">
                  <span>{{ dogId }}: 
                    (
                    {{ selectedConformationEntryDetails.winnerDogId?.trim() == dogId ? 'WD ' : '' }}
                    {{ selectedConformationEntryDetails.winnerBitchId?.trim() == dogId ? 'WB ' : '' }}
                    {{ selectedConformationEntryDetails.bestOfBreedId?.trim() == dogId ? 'BOB ' : '' }}
                    {{ selectedConformationEntryDetails.bestOfOppositeSexId?.trim() == dogId ? 'BOS ' : '' }}
                    {{ selectedConformationEntryDetails.selectDogId?.trim() == dogId ? 'SelD ' : '' }}
                    {{ selectedConformationEntryDetails.selectBitchId?.trim() == dogId ? 'SelB ' : '' }}
                    ),
                    Score:
                    {{ selectedConformationEntryDetails.winnerDogId?.trim() == dogId ? calculatedPoints.winnerDogScore : '' }}
                    {{ selectedConformationEntryDetails.winnerBitchId?.trim() == dogId ? calculatedPoints.winnerBitchScore : '' }},
                    #Cmp: 
                    {{ selectedConformationEntryDetails.bestOfBreedId?.trim() == dogId ? calculatedPoints.bestOfBreedScore : '' }}
                    {{ selectedConformationEntryDetails.bestOfOppositeSexId?.trim() == dogId ? calculatedPoints.bestOfOppositeSexScore : '' }},
                    SCCH:                    
                    {{ selectedConformationEntryDetails.bestOfBreedId?.trim() == dogId ? calculatedPoints.scchBestOfBreedScore : '' }}
                    {{ selectedConformationEntryDetails.bestOfOppositeSexId?.trim() == dogId ? calculatedPoints.scchBestOfOppositeSexScore : '' }}
                    {{ selectedConformationEntryDetails.selectDogId?.trim() == dogId ? calculatedPoints.selectDogScore : '' }}
                    {{ selectedConformationEntryDetails.selectBitchId?.trim() == dogId ? calculatedPoints.selectBitchScore : '' }}
                  </span>
                  <br/>
                </span>
              </p>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
      <table class="pl-5 pr-7">
        <tr>
            <th>Dog ID</th>
            <th>Date</th>
            <th>Time</th>
            <th>Category</th>
            <th>PS
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon color="darkGray" class="ml-1" dark v-bind="attrs" v-on="on">
                    mdi-information
                  </v-icon>
                </template>
                  <span v-for="sched in showInfo.affiliatePointSchedules" :key="`${sched.id}-${sched.displayValue}`">
                    {{sched.displayValue}}: <span v-if="!!sched.id">{{sched.id}}</span><span v-else>N/A</span><br/>
                  </span>
              </v-tooltip>
            </th>
            <th>Judge</th>
            <th>Score</th>
            <th>Handler</th>
            <th># Cmp</th>
            <th>SCCH Score</th>
            <th>SCCH Major</th>
            <th></th> <!-- actions column -->
        </tr>
        <tr :id="`#sr-empty`" v-if="!!emptyRow" class="orange lighten-4">
                  <td class="pr-1" :style="`text-align: left;`">
                    <v-text-field
                        style="width: 175px;"
                        id="srEmptyDogId"
                        ref="srEmptyDogId"
                        v-model="emptyRow.dogId"
                      />
                  </td>
                  <td class="pr-1 pl-1">             
                    <DatepickerFormatted 
                      :disableClearable="true"
                      :date="emptyRow.showDate"
                      v-on:updateDates="($event) => updateDates($event, 'showDate', emptyRow)" />
                  </td>
                  <td class="pr-1 pl-1">             
                    <v-text-field
                        v-model="emptyRow.showTime"
                      />
                  </td>
                  <td class="pr-1 pl-1">
                    <v-autocomplete
                      v-model="emptyRow.categoryId"
                      item-value="id"
                      item-text="displayValue"
                      :items="showInfo.categories"
                      :rules="[(v) => !!v || 'Required']"
                    />
                  </td>
                  <td class="pr-1 pl-1">
                    <v-autocomplete
                      v-model="emptyRow.pointSchedule"
                      item-value="id"
                      item-text="displayValue"
                      :items="pointSchedules"
                    />
                  </td>
                  <td class="pr-1 pl-1">
                    <v-autocomplete
                      v-model="emptyRow.judgeId"
                      item-value="id"
                      item-text="displayValue"
                      :items="showInfo.judges"
                      :rules="[(v) => !!v || 'Required']"
                    />
                  </td>
                  <td class="pr-1 pl-1">
                    <v-text-field
                      v-model="emptyRow.score"
                      type="numeric"
                      :rules="[(v) => !isNaN(v) || 'Numeric value required']"
                    />
                  </td>
                  <td class="pr-1 pl-1">
                    <v-text-field
                      v-model="emptyRow.dogHandler"
                    />
                  </td>
                  <td class="pr-1 pl-1">
                    <v-text-field
                      v-model="emptyRow.competingCount"
                      type="numeric"
                      :rules="[(v) => !isNaN(v) || 'Numeric value required']"
                    />
                  </td>
                  <td class="pr-1 pl-1">
                    <v-text-field
                      v-model="emptyRow.scchScore"
                      type="numeric"
                      :rules="[(v) => !isNaN(v) || 'Numeric value required']"
                    />
                  </td>                  
                  <td class="pr-1 pl-1">
                    <!-- is SCCH major is calculated automatically for the new result -->
                    Auto-calculated: <span v-if="emptyRow.scchScore == null || emptyRow.scchScore == ''">N/A</span><span v-else>{{ emptyRow.scchScore >= 3 ? 'Yes' : 'No' }}</span>
                  </td>
                  <td class="pl-1" style="width: 60px">
                    <v-icon class="mr-1" color="primary" @click="saveShowResult(emptyRow, showResults.length - 1)">
                      mdi-content-save-edit
                    </v-icon>
                  </td>
                </tr>
        </table>
        <v-divider inset />
        <v-divider inset />
        <v-virtual-scroll
          :items="showResults"
          :item-height="100"
          height="680"
          content-tag="table"
          id="ConformationShowResultsScroller"
          >
          <template v-slot:default="{ item, index }">
            <v-list-item key="item.resultId" :id="`sr-${item.resultId}`">
              <v-list-item-content>
                <tr :class="row_classes(item, index)" :id="`#sr-${item.resultId}`">
                  <td class="pr-1">
                    <v-text-field
                        v-model="item.dogId"
                      />
                        <v-tooltip bottom v-if="item.dogName">
                            <template v-slot:activator="{ on, attrs }">
                                <span class="text--secondary caption d-inline-block  text-truncate" style="width: 175px;"
                                    v-bind="attrs"
                                    v-on="on">
                                    <a tabindex="-1" :href="`/admin/dogs/${encodeURIComponent(item.dogId)}`" target="_blank" v-if="item.dogId">{{item.dogName}}</a>
                                </span>
                            </template>
                            <span>{{item.dogName}}</span>
                        </v-tooltip>
                        <v-tooltip bottom v-else>
                            <template v-slot:activator="{ on, attrs }">
                                <span  class="text--secondary  caption d-inline-block  text-truncate" style="width: 175px;"
                                    v-bind="attrs"
                                    v-on="on">
                                    <a tabindex="-1" :href="`/admin/dogs/${encodeURIComponent(item.dogId)}`" target="_blank" v-if="item.dogId">{{item.dogName}}</a>
                                </span>
                            </template>
                            <span>{{item.dogName}}</span>
                        </v-tooltip>
                  </td>
                  <td class="pr-1 pl-1">             
                    <DatepickerFormatted 
                      :disableClearable="true"
                      :date="item.showDate"
                      v-on:updateDates="($event) => updateDates($event, 'showDate', item)" />
                  </td>
                  <td class="pr-1 pl-1">             
                    <v-text-field
                        v-model="item.showTime"
                      />
                  </td>
                  <td class="pr-1 pl-1">
                    <v-autocomplete
                      v-model="item.categoryId"
                      item-value="id"
                      item-text="displayValue"
                      :items="showInfo.categories"
                      :rules="[(v) => !!v || 'Required']"
                    />
                  </td>
                  <td class="pr-1 pl-1">
                    <v-autocomplete
                      v-model="item.pointSchedule"
                      item-value="id"
                      item-text="displayValue"
                      :items="pointSchedules"
                    />
                  </td>
                  <td class="pr-1 pl-1">
                    <v-autocomplete
                      v-model="item.judgeId"
                      item-value="id"
                      item-text="displayValue"
                      :items="showInfo.judges"
                      :rules="[(v) => !!v || 'Required']"
                    />
                  </td>
                  <td class="pr-1 pl-1">
                    <v-text-field
                      v-model="item.score"
                      type="numeric"
                      @input="item.score = $event !== '' ? $event : null"
                      :rules="[(v) => !isNaN(v) || 'Numeric value required']"
                    />
                  </td>
                  <td class="pr-1 pl-1">
                    <v-text-field
                      v-model="item.dogHandler"
                    />
                  </td>
                  <td class="pr-1 pl-1">
                    <v-text-field
                      v-model="item.competingCount"
                      type="numeric"
                      :rules="[(v) => !isNaN(v) || 'Numeric value required']"
                    />
                  </td>
                  <td class="pr-1 pl-1">
                    <v-text-field
                      v-model="item.scchScore"
                      type="numeric"
                      @input="item.scchScore = $event !== '' ? $event : null"
                      :rules="[(v) => !isNaN(v) || 'Numeric value required']"
                    />
                  </td>                  
                  <td class="pr-1 pl-1">
                    <v-checkbox
                      :indeterminate="item.isScchMajor == null"
                      v-model="item.isScchMajor" 
                    />
                  </td>
                  <td class="pl-1" style="width: 60px">
                    <v-icon class="mr-1" color="primary" @click="saveShowResult(item, index)">
                      mdi-content-save-edit
                    </v-icon>
                    <v-icon :disabled="!item.resultId" tabindex="-1" @click="deleteShowResult(item)">
                      mdi-delete
                    </v-icon>
                  </td>
                </tr>
              </v-list-item-content>
            </v-list-item>
          </template>
        </v-virtual-scroll>
    
  </v-sheet>
</template>

<script lang="ts">
import Vue from "vue";
import * as _ from "lodash";
import DatepickerFormatted from "../../../common/inputs/DatepickerFormatted.vue";
import showService from "@/services/admin/showService";

export default Vue.extend({
  name: "ConformationShowResultsEntry",
  components: {
    DatepickerFormatted
  },
  data: function() {
    return {
      selectedConformationEntry: null as any,
      selectedConformationEntryDetails: null as any,
      savedConformationEntries: [] as any[],
      calculatedPoints: null as any,

      showDeleteConfirmationDialog: false,
      deletedShowResultId: null,
      showResults: [] as any[],
      persistedShowResults: [] as any[],
      errorMessage: null as any,
      emptyRow: null as any,
      uniqueDogIds: [] as any,
      checkboxStates: [null, true, false],
    };
  },
  methods: {    
    emptyShowResultRow: function(lastPersistedRow: any, showResults: any[]){
      if(!lastPersistedRow){
        if(!!showResults && showResults.length > 0){
          lastPersistedRow = Object.assign({}, showResults[showResults.length - 1]);
        }
      }

      if(!!lastPersistedRow){
        return {
          resultId: null,
          isNewRow: true,
          dogId: null,
          showDate: lastPersistedRow.showDate,
          showTime: lastPersistedRow.showTime,
          judgeId: lastPersistedRow.judgeId,
          categoryId: lastPersistedRow.categoryId,
          competingCount: null,
          score: null,
          scchScore: null,
          isScchMajor: null,
          showId: this.showId,
          pointSchedule: null,
        };
      } else {
        return {
          resultId: null,
          isNewRow: true,
          dogId: null,
          showDate: this.showInfo.defaultDate,
          showTime: null,
          categoryId: null,
          judgeId: (!!this.showInfo.judges && this.showInfo.judges.length == 1) ? this.showInfo.judges[0].id : null,
          score: null,
          scchScore: null,
          isScchMajor: null,
          competingCount: null,
          showId: this.showId,
          pointSchedule: null,
        };
      }
    },  
    reloadData: function() {
      if (this.showId) {
        this.$store.dispatch("changeLoaderStatus",true);

        try {
          showService.getConformationShowResultsSummaryList(this.showId)
          .then((res) => { 
            this.savedConformationEntries = res; 
            this.selectedConformationEntry = null;
          }
          ).finally(() => { });

        showService
            .getShowResultsByShowId(this.showId, 'C')
            .then((res) => {
                this.showResults = _.cloneDeep(res);
                this.persistedShowResults = _.cloneDeep(res);
                this.emptyRow = Object.assign({}, this.emptyShowResultRow(null, res));
            })
            .catch(this.$handleError)
            .finally(() => { }); 
          } finally {
            this.$store.dispatch("changeLoaderStatus",false);
          }
      }
    }, 
    row_classes: function(item: any, index: number){
      if (!!item.resultId)
      {
        const index = this.showResults.findIndex(i => i.resultId === item.resultId);
        if(_.isEqual(this.showResults[index], this.persistedShowResults[index])){
          return '';
        }
      } 
      return 'orange  lighten-4';
    },
    saveShowResult: function(item: any, index: any) {
      this.$store.dispatch("changeLoaderStatus",true);
      const isAddingNewRow = !item.resultId;
      showService.updateShowResult(this.showId, 'C', item)
        .then((res) => {
          if(!res.success){
            this.errorMessage = res.messages.join(", ");
            if(isAddingNewRow){
              const input = document.querySelector('#srEmptyDogId') as HTMLElement | null;

              if (input != null) {
                  input.focus();
              }
            }
          } else {
            if(isAddingNewRow){
              this.showResults.unshift(res.showResult);
              this.persistedShowResults = _.cloneDeep(this.showResults);

              this.emptyRow = Object.assign({}, this.emptyShowResultRow(this.emptyRow, this.showResults));
              const input = document.querySelector('#srEmptyDogId') as HTMLElement | null;

              if (input != null) {
                  input.focus();
              }
            } else {
              //in case the dogId value has been changed we need to update the dog name to allow admin to validate data entry
              this.showResults[index] =  Object.assign({}, res.showResult);
              this.persistedShowResults = _.cloneDeep(this.showResults);
            }
            this.errorMessage = null;
          }
        })
        .catch((e) => {
          this.errorMessage = "Changes couldn't be saved. Ensure there are no validation errors before retrying.";
        })
        .finally(() => { this.$store.dispatch("changeLoaderStatus",false);}); 
    },
    deleteShowResult: function(item: any) {
      if(item.resultId) {//if that item has been persisted already
        this.deletedShowResultId = item.resultId;
        this.showDeleteConfirmationDialog = true;
      } else {
        const index = this.showResults.indexOf(item);
        this.showResults.splice(index, 1);
      }
    },
    confirmDelete: function() {
      showService.deleteShowResult(this.showId, this.deletedShowResultId)
      .then((res) => {
        if(!res.success){
          const index = this.showResults.map(sr => sr.resultId).indexOf(this.deletedShowResultId);
          this.showResults[index].errors = res.messages.join(", ");
          this.deletedShowResultId = null;
        } else {
          const index = this.showResults.map(sr => sr.resultId).indexOf(this.deletedShowResultId);
          this.showResults.splice(index, 1);
          this.persistedShowResults.splice(index, 1);
        }   
      })
      .finally(() => {this.showDeleteConfirmationDialog = false});
    },
    updateDates: function (val: any, key: string, item: any) {
      item[key] = val?.date;
      //workaround to force rerendering after setting up formatted date
      this.$set(this, 'showResults', this.showResults);
    },
    calculatePoints: function() {
      this.$store.dispatch("changeLoaderStatus",true);
      try{
        showService.getConformationShowResultsSummary(this.showId, this.selectedConformationEntry.showDate, this.selectedConformationEntry.showTime, this.selectedConformationEntry.showType)
        .then((res) => { 
          this.selectedConformationEntryDetails = _.cloneDeep(res);
        });

        showService.calculatePointsForConformationShowResults(this.showId, this.selectedConformationEntry.showDate, this.selectedConformationEntry.showTime, this.selectedConformationEntry.showType)
          .then((res: any) =>{ 
            this.calculatedPoints = res;
            this.uniqueDogIds = new Set();
            if(!!this.selectedConformationEntryDetails?.winnerDogId){
              this.uniqueDogIds.add(this.selectedConformationEntryDetails.winnerDogId?.trim());
            }
            if(!!this.selectedConformationEntryDetails?.winnerBitchId){
              this.uniqueDogIds.add(this.selectedConformationEntryDetails.winnerBitchId?.trim());
            }
            if(!!this.selectedConformationEntryDetails?.bestOfBreedId){
              this.uniqueDogIds.add(this.selectedConformationEntryDetails.bestOfBreedId?.trim());
            }
            if(!!this.selectedConformationEntryDetails?.bestOfOppositeSexId){
              this.uniqueDogIds.add(this.selectedConformationEntryDetails.bestOfOppositeSexId?.trim());
            }
            if(!!this.selectedConformationEntryDetails?.selectDogId){
              this.uniqueDogIds.add(this.selectedConformationEntryDetails.selectDogId?.trim());
            }
            if(!!this.selectedConformationEntryDetails?.selectBitchId){
              this.uniqueDogIds.add(this.selectedConformationEntryDetails.selectBitchId?.trim());
            }
          })
      } finally { this.$store.dispatch("changeLoaderStatus",false); };  
    },
  },    
  created: function() {
    this.reloadData();
  },
  watch: {
    showId: function(newVal: String, oldVal: String) {
      if(newVal && newVal != oldVal){
        this.reloadData();
      }
    },    
    "selectedConformationEntry": function(newVal: String, oldVal: String) {
      if(newVal != oldVal){
        this.$set(this, 'selectedConformationEntryDetails', null);
        this.$set(this, 'calculatedPoints', null);
      }
    },
  },
  computed: {
    pointSchedules: function(){
      if(!this.showInfo || !this.showInfo.affiliatePointSchedules)
      {
        return [];
      }

      const uniqueIds = _.uniq(this.showInfo.affiliatePointSchedules.map((ps: any) => ps.id)).filter(id => !!id);
      let ptSched = [] as any[];
      uniqueIds.forEach((x: any) => {
        const matching = this.showInfo.affiliatePointSchedules.filter((ps: any) => ps.id == x);
        const entry = {id: x, displayValue: `${x}: ${matching.map((ps:any) => ps.displayValue).join(", ")}`}
        ptSched.push(entry);
      });
      return ptSched;
    }
  },
  props: {
    showId: { type: String },
    showInfo: {type: Object}
  },
});
</script>
